import {
	ProjectAnnualDataAttribute,
	ProjectAnnualService,
	ProjectAttribute,
	ProjectAttributeLog,
	ProjectAttributeType,
	ProjectMonthlyDataAttribute,
	ProjectMonthlyService,
	ProjectPeriodicDataAttributeType,
	ProjectQuarterlyDataAttribute,
	ProjectQuarterlyService,
	ProjectRequisitionAnnualDataAttribute,
	ProjectRequisitionAnnualDataAttributeLog,
	ProjectService,
} from 'api';
import useRequest, { useImmerRequest } from 'api/core/useRequest';
import { ReportStatusEnum } from 'api/enums';
import { useMaxYearFinance } from 'components/annualreport/company/AnnualReportCompany.utils';

import { useProject } from 'lib/context/project-context';
import ErrorMessage from 'lib/designsystem/error/ErrorMessage';
import LoadingCentered from 'lib/designsystem/LoadingCentered/LoadingCentered';
import { groupBy } from 'lib/utils/groupBy';
import { Updater } from 'lib/utils/useImmer';
import { createContext, Dispatch, ReactNode, SetStateAction, useContext, useMemo, useState } from 'react';

type ProjectDetailsContextType = {
	/**Project ID */
	projectId: number;

	/**Project attribute logs */
	attributeLogs: readonly ProjectAttributeLog[];

	/**All annual attributes */
	annualAttributes: readonly ProjectAnnualDataAttribute[];

	/**All quarterly attributes */
	quarterlyAttributes: readonly ProjectQuarterlyDataAttribute[];

	/**All monthly attributes */
	monthlyAttributes: readonly ProjectMonthlyDataAttribute[];

	/**Annual attributes for requisition subprojects */
	annualRequisitionAttributes: readonly ProjectRequisitionAnnualDataAttribute[];

	/**All annual requisition attribute logs */
	annualRequisitionAttributeLogs: readonly ProjectRequisitionAnnualDataAttributeLog[];

	/**Updater function for attribute logs */
	updateAttributeLogs: Updater<ProjectAttributeLog[]>;

	/**Updater function for quarterly attributes */
	updateAnnualAttributes: Updater<ProjectAnnualDataAttribute[]>;

	/**Updater function for quarterly attributes */
	updateQuarterlyAttributes: Updater<ProjectQuarterlyDataAttribute[]>;

	/**Updater function for monthly attributes */
	updateMonthlyAttributes: Updater<ProjectMonthlyDataAttribute[]>;

	/**Refetch all project attribute logs (i.e. synchronize with backend / database) */
	refetchLogs: () => void;

	/**Last year where an annual report was submitted */
	yearLatestAnnualReport: number | undefined;

	editMode: boolean;

	setEditMode: Dispatch<SetStateAction<boolean>>;
};

type ContextProps = {
	projectId: number;
	includeQuarterlyData?: boolean;
	includeMonthlyData?: boolean;
	children: ReactNode;
};

export const ProjectDetailsContext = createContext<ProjectDetailsContextType | undefined>(undefined);

/**Context for a single project (bompengeprosjekt). Use as a local context when needed.  */
export default function ProjectDetailsProvider({
	projectId,
	includeQuarterlyData = true,
	includeMonthlyData = true,
	children,
}: ContextProps) {
	const [editMode, setEditMode] = useState<boolean>(false);

	const {
		data: annualAttributes = [],
		updateData: updateAnnualAttributes,
		isLoading: isLoadingAnnualAttributes,
		error: errorAnnualAttributes,
	} = useImmerRequest(
		ProjectAnnualService.findAnnualAttributesForProject,
		{ projectId: projectId },
		{
			gcTime: 604_800_000, // 1 week
			staleTime: 0,
		}
	);

	const {
		data: quarterlyAttributes = [],
		updateData: updateQuarterlyAttributes,
		isLoading: isLoadingQuarterlyAttributes,
		error: errorQuarterlyAttributes,
	} = useImmerRequest(
		ProjectQuarterlyService.findQuarterlyAttributesForProject,
		{ projectId: projectId },
		{
			gcTime: 604_800_000, // 1 week
			staleTime: 0,
			enabled: includeQuarterlyData,
		}
	);

	const {
		data: monthlyAttributes = [],
		updateData: updateMonthlyAttributes,
		isLoading: isLoadingMonthlyAttributes,
		error: errorMonthlyAttributes,
	} = useImmerRequest(
		ProjectMonthlyService.findMonthlyAttributesForProject,
		{ projectId: projectId },
		{
			gcTime: 604_800_000, // 1 week
			staleTime: 0,
			enabled: includeMonthlyData,
		}
	);

	const { data: annualRequisitionAttributeLogs = [] } = useImmerRequest(
		ProjectAnnualService.findAnnualRequisitionAttributeLogs,
		{ projectId: [projectId] },
		{
			gcTime: 604_800_000, // 1 week
			staleTime: 0,
		}
	);

	const {
		data: annualRequisitionAttributes = [],
		isLoading: isLoadingAnnualRequisitionAttributes,
		error: errorAnnualRequisitionAttributes,
	} = useRequest(
		ProjectAnnualService.findAnnualAttributesForProjectRequisition,
		{ projectId: projectId },
		{
			gcTime: 604_800_000, // 1 week
			staleTime: 0,
		}
	);

	const {
		data: attributeLogs = [],
		updateData: updateAttributeLogs,
		isLoading: isLoadingAttributeLogs,
		error: errorAttributeLogs,
		refetch: refetchLogs,
	} = useImmerRequest(
		ProjectService.findAttributeLogsForProject,
		{ projectId: projectId },
		{
			gcTime: 604_800_000, // 1 week
			staleTime: 0,
		}
	);

	if (errorAnnualAttributes) {
		return <ErrorMessage error={errorAnnualAttributes} />;
	}

	if (errorQuarterlyAttributes) {
		return <ErrorMessage error={errorQuarterlyAttributes} />;
	}

	if (errorMonthlyAttributes) {
		return <ErrorMessage error={errorMonthlyAttributes} />;
	}

	if (errorAttributeLogs) {
		return <ErrorMessage error={errorAttributeLogs} />;
	}

	if (errorAnnualRequisitionAttributes) {
		return <ErrorMessage error={errorAnnualRequisitionAttributes} />;
	}

	if (
		isLoadingAnnualAttributes ||
		isLoadingQuarterlyAttributes ||
		isLoadingAttributeLogs ||
		isLoadingMonthlyAttributes ||
		isLoadingAnnualRequisitionAttributes
	) {
		return <LoadingCentered />;
	}

	const yearsWithAnnualReport = annualAttributes
		.filter((attribute) => attribute.attributeType === 'REPORT_STATUS')
		.filter((attribute) => attribute.value !== ReportStatusEnum.DRAFT._name)
		.map((attribute) => attribute.year);

	const yearLatestAnnualReport = yearsWithAnnualReport.length ? Math.max(...yearsWithAnnualReport) : undefined;

	const value = {
		projectId: projectId,
		attributeLogs,
		annualAttributes,
		quarterlyAttributes,
		monthlyAttributes,
		annualRequisitionAttributes,
		annualRequisitionAttributeLogs,
		updateAttributeLogs,
		updateAnnualAttributes,
		updateQuarterlyAttributes,
		updateMonthlyAttributes,
		refetchLogs,
		yearLatestAnnualReport,
		editMode,
		setEditMode,
	} as ProjectDetailsContextType;

	return <ProjectDetailsContext.Provider value={value}>{children}</ProjectDetailsContext.Provider>;
}

export function useProjectDetailsContext(): ProjectDetailsContextType {
	const context = useContext(ProjectDetailsContext);
	if (context === undefined) {
		throw new Error(`useProjectDetailsContext() must be used within a ProjectDetailsProvider`);
	}
	return context;
}

/**Get all attribute logs */
export function useAllAttributeLogs(): readonly ProjectAttributeLog[] {
	const { attributeLogs } = useProjectDetailsContext();
	return attributeLogs;
}

export function useGetAttributeLogs(): (attributeType: ProjectAttributeType, incrId?: number) => ProjectAttributeLog[] {
	const logs: readonly ProjectAttributeLog[] = useAllAttributeLogs();

	// attributeType|incrid => attributeLog[]
	const grouped = useMemo(() => {
		return groupBy([...logs], (data) => data.attributeType + '|' + data.incrId);
	}, [logs]);

	const getAttributeLogs = (attributeType: ProjectAttributeType, incrId: number = 0) => {
		const key: string = attributeType + '|' + incrId;
		return (grouped.get(key) ?? []).sort((a, b) => {
			if (!!a?.id && !!b?.id && a.validFrom === b.validFrom) {
				return a.id > b.id ? 1 : -1;
			}
			return a.validFrom > b.validFrom ? 1 : -1;
		});
	};

	return getAttributeLogs;
}

export function useRefetchLogs(): () => void {
	const context = useContext(ProjectDetailsContext);
	if (context === undefined) {
		throw new Error(`useRefetchLogs must be used within a ProjectDetailsProvider`);
	}
	return context.refetchLogs;
}

/**ProjectAttributes may change value, this function can be used to look up historical values */
export function useGetAttributeValueAtDate(): (
	dt: Date,
	attributeType: ProjectAttributeType,
	incrId?: number
) => string | undefined {
	const { projectId, attributeLogs } = useProjectDetailsContext();
	const projectDTO = useProject(projectId);

	// attributeType|incrid => attribute
	const attributeMap = useMemo(() => {
		return new Map(
			projectDTO?.attributes.map((attribute) => [attribute.attributeType + '|' + attribute.incrId, attribute])
		);
	}, [projectDTO]);

	// attributeType|incrid => attributeLog[]
	const grouped = useMemo(() => {
		return groupBy(attributeLogs as ProjectAttributeLog[], (data) => data.attributeType + '|' + data.incrId);
	}, [attributeLogs]);

	const getAttributeValueAtDate = (dt: Date, attributeType: ProjectAttributeType, incrId: number = 0) => {
		const key: string = attributeType + '|' + incrId;
		const current: ProjectAttribute | undefined = attributeMap.get(key);
		if (!current) {
			return undefined;
		}
		if (new Date(current.validFrom) < dt) {
			return current.value;
		}
		const logs: ProjectAttributeLog[] = grouped.get(key) ?? [];
		const validLogs = logs.filter((log) => new Date(log.validFrom) < dt);
		if (validLogs.length === 0) {
			return current.value;
		}
		const latest: ProjectAttributeLog = validLogs.reduce((max, obj) => (max.validFrom > obj.validFrom ? max : obj));
		return latest.value;
	};

	return getAttributeValueAtDate;
}

/**Get all annual attributes */
export function useAnnualAttributes(): readonly ProjectAnnualDataAttribute[] {
	const context = useContext(ProjectDetailsContext);
	if (context === undefined) {
		throw new Error(`useAnnualAttributes must be used within a ProjectDetailsProvider`);
	}
	return context.annualAttributes;
}

/**Get attribute for annual data */
export function useGetAnnualAttribute(): (
	attributeType: ProjectPeriodicDataAttributeType,
	year: number,
	incrId?: number
) => ProjectAnnualDataAttribute | undefined {
	const context = useContext(ProjectDetailsContext);
	if (context === undefined) {
		throw new Error(`useGetAnnualAttribute must be used within a ProjectDetailsProvider`);
	}
	const geAnnualAttribute = (attributeType: ProjectPeriodicDataAttributeType, year: number, incrId: number = 0) => {
		return context.annualAttributes?.find(
			(attribute) => attribute.attributeType === attributeType && attribute.year === year && attribute.incrId === incrId
		);
	};
	return geAnnualAttribute;
}

/**Get all annual attributes of a given attributeType */
export function useGetAnnualAttributes(): (
	attributeType: ProjectPeriodicDataAttributeType
) => ProjectAnnualDataAttribute[] {
	const context = useContext(ProjectDetailsContext);
	if (context === undefined) {
		throw new Error(`useGetAnnualAttributes must be used within a ProjectDetailsProvider`);
	}
	const geAnnualAttributes = (attributeType: ProjectPeriodicDataAttributeType) => {
		return context.annualAttributes?.filter((attribute) => attribute.attributeType === attributeType) ?? [];
	};
	return geAnnualAttributes;
}

/**Get attribute value for annual data */
export function useGetAnnualAttributeValue(): (
	attributeType: ProjectPeriodicDataAttributeType,
	year: number,
	incrId?: number
) => string | undefined {
	const geAnnualAttribute = useGetAnnualAttribute();
	const getAnnualAttributeValue = (
		attributeType: ProjectPeriodicDataAttributeType,
		year: number,
		incrId: number = 0
	) => {
		const attribute = geAnnualAttribute(attributeType, year, incrId);
		return attribute?.value;
	};
	return getAnnualAttributeValue;
}

/**Update a list of annual attributes */
export function useUpdateAnnualAttributes(): (
	projectId: number,
	updatedAttributes: ProjectAnnualDataAttribute[]
) => Promise<void> {
	const context = useContext(ProjectDetailsContext);
	if (context === undefined) {
		throw new Error(`useUpdateAnnualAttributes must be used within a ProjectDetailsProvider`);
	}
	// Update provided attributes, and synchronize context with backend afterwards
	const update = async (projectId: number, updatedAttributes: ProjectAnnualDataAttribute[]) => {
		if (updatedAttributes.length === 0) {
			return;
		}
		// Method returns all attributes for the project
		const allAttributes: ProjectAnnualDataAttribute[] = await ProjectAnnualService.updateAnnualAttributesForProject({
			projectId: projectId,
			requestBody: updatedAttributes,
		});
		// Update 'state' before returning
		await context.updateAnnualAttributes(() => {
			return allAttributes;
		});
		return;
	};

	return update;
}

/**Get all quarterly attributes */
export function useQuarterlyAttributes(): readonly ProjectQuarterlyDataAttribute[] {
	const context = useContext(ProjectDetailsContext);
	if (context === undefined) {
		throw new Error(`useQuarterlyAttributes must be used within a ProjectDetailsProvider`);
	}
	return context.quarterlyAttributes;
}

/**Get attribute value for quarterly data */
export function useGetQuarterlyAttributeValue(): (
	attributeType: ProjectPeriodicDataAttributeType,
	quarter: number,
	year: number,
	incrId?: number
) => string | undefined {
	const getQuarterlyAttribute = useGetQuarterlyAttribute();
	const getQuarterlyAttributeValue = (
		attributeType: ProjectPeriodicDataAttributeType,
		quarter: number,
		year: number,
		incrId: number = 0
	) => {
		const attribute = getQuarterlyAttribute(attributeType, quarter, year, incrId);
		return attribute?.value;
	};

	return getQuarterlyAttributeValue;
}

/**Get attribute for quarterly data */
export function useGetQuarterlyAttribute(): (
	attributeType: ProjectPeriodicDataAttributeType,
	quarter: number,
	year: number,
	incrId?: number
) => ProjectQuarterlyDataAttribute | undefined {
	const context = useContext(ProjectDetailsContext);
	if (context === undefined) {
		throw new Error(`useGetQuarterlyAttribute must be used within a ProjectDetailsProvider`);
	}
	const getQuarterlyAttribute = (
		attributeType: ProjectPeriodicDataAttributeType,
		quarter: number,
		year: number,
		incrId: number = 0
	) => {
		return context.quarterlyAttributes?.find(
			(attribute) =>
				attribute.attributeType === attributeType &&
				attribute.quarter === quarter &&
				attribute.year === year &&
				attribute.incrId === incrId
		);
	};
	return getQuarterlyAttribute;
}

/**Update a list of quarterly attributes */
export function useUpdateQuarterlyAttributes(): (
	projectId: number,
	updatedAttributes: ProjectQuarterlyDataAttribute[]
) => Promise<void> {
	const context = useContext(ProjectDetailsContext);
	if (context === undefined) {
		throw new Error(`useUpdateQuarterlyAttributes must be used within a ProjectDetailsProvider`);
	}
	// Update provided attributes, and synchronize context with backend afterwards
	const update = async (projectId: number, updatedAttributes: ProjectQuarterlyDataAttribute[]) => {
		if (updatedAttributes.length === 0) {
			return;
		}
		// Method returns all attributes for the project
		const allAttributes: ProjectQuarterlyDataAttribute[] =
			await ProjectQuarterlyService.updateQuarterlyAttributesForProject({
				projectId: projectId,
				requestBody: updatedAttributes,
			});
		// Update 'state' before returning
		await context.updateQuarterlyAttributes(() => {
			return allAttributes;
		});
		return;
	};

	return update;
}

/**Update a list of monthly attributes */
export function useUpdateMonthlyAttributes(): (
	projectId: number,
	updatedAttributes: ProjectMonthlyDataAttribute[]
) => Promise<void> {
	const context = useContext(ProjectDetailsContext);
	if (context === undefined) {
		throw new Error(`useUpdateMonthlyAttributes must be used within a ProjectDetailsProvider`);
	}
	// Update provided attributes, and synchronize context with backend afterwards
	const update = async (projectId: number, updatedAttributes: ProjectMonthlyDataAttribute[]) => {
		if (updatedAttributes.length === 0) {
			return;
		}
		// Method returns all attributes for the project
		const allAttributes: ProjectMonthlyDataAttribute[] = await ProjectMonthlyService.updateMonthlyAttributesForProject({
			projectId: projectId,
			requestBody: updatedAttributes,
		});
		// Update 'state' before returning
		await context.updateMonthlyAttributes(() => {
			return allAttributes;
		});
		return;
	};

	return update;
}

/**Get all monthly attributes */
export function useMonthlyAttributes(): readonly ProjectMonthlyDataAttribute[] {
	const context = useContext(ProjectDetailsContext);
	if (context === undefined) {
		throw new Error(`useMonthlyAttributes must be used within a ProjectDetailsProvider`);
	}
	return context.monthlyAttributes;
}

/**Get attribute for monthly data */
export function useGetMonthlyAttribute(): (
	attributeType: ProjectPeriodicDataAttributeType,
	month: number,
	year: number,
	incrId?: number
) => ProjectMonthlyDataAttribute | undefined {
	const context = useContext(ProjectDetailsContext);
	if (context === undefined) {
		throw new Error(`useGetMonthlyAttribute must be used within a ProjectDetailsProvider`);
	}
	const getMonthlyAttribute = (
		attributeType: ProjectPeriodicDataAttributeType,
		month: number,
		year: number,
		incrId: number = 0
	) => {
		return context.monthlyAttributes?.find(
			(attribute) =>
				attribute.attributeType === attributeType &&
				attribute.month === month &&
				attribute.year === year &&
				attribute.incrId === incrId
		);
	};
	return getMonthlyAttribute;
}

/**Get attribute value for monthly data */
export function useGetMonthlyAttributeValue(): (
	attributeType: ProjectPeriodicDataAttributeType,
	month: number,
	year: number,
	incrId?: number
) => string | undefined {
	const getMonthlyAttribute = useGetMonthlyAttribute();
	const getMonthlyAttributeValue = (
		attributeType: ProjectPeriodicDataAttributeType,
		month: number,
		year: number,
		incrId: number = 0
	) => {
		const attribute = getMonthlyAttribute(attributeType, month, year, incrId);
		return attribute?.value;
	};

	return getMonthlyAttributeValue;
}

/**Get attribute value for monthly data */
export function useYearLatestAnnualReport(): number | undefined {
	const context = useContext(ProjectDetailsContext);
	if (context === undefined) {
		throw new Error(`useYearLatestAnnualReport must be used within a ProjectDetailsProvider`);
	}
	return context.yearLatestAnnualReport;
}

/**Get all annual requisition attributes */
export function useAnnualRequisitionAttributes(): readonly ProjectRequisitionAnnualDataAttribute[] {
	const context = useContext(ProjectDetailsContext);
	if (context === undefined) {
		throw new Error(`useAnnualRequisitionAttributes must be used within a ProjectDetailsProvider`);
	}
	return context.annualRequisitionAttributes;
}

/**Get requisition attribute for annual data */
export function useGetAnnualRequisitionAttribute(): (
	projectRequisitionId: number,
	attributeType: ProjectPeriodicDataAttributeType,
	year: number,
	incrId?: number
) => ProjectRequisitionAnnualDataAttribute | undefined {
	const context = useContext(ProjectDetailsContext);
	if (context === undefined) {
		throw new Error(`useGetAnnualAttribute must be used within a ProjectDetailsProvider`);
	}
	const geAnnualRequisitionAttribute = (
		projectRequisitionId: number,
		attributeType: ProjectPeriodicDataAttributeType,
		year: number,
		incrId: number = 0
	) => {
		return context.annualRequisitionAttributes?.find(
			(attribute) =>
				attribute.projectRequisitionId === projectRequisitionId &&
				attribute.attributeType === attributeType &&
				attribute.year === year &&
				attribute.incrId === incrId
		);
	};
	return geAnnualRequisitionAttribute;
}

/**Is finance data accessible for the selected year */
export function useFinanceDataAccessible({ year }: { year: number }): boolean {
	const maxYearFinance = useMaxYearFinance();
	return maxYearFinance && maxYearFinance < year ? false : true;
}
